/**
 * HUD layout for any screen (GDD §7.2, §8; judgment calls 26, 27, 32). The HUD is drawn at the concept board's size
 * and scaled: landscape at 1280×720 (frame 01), portrait at 390×844 (frame 04). Panels and controls have their own
 * scales, so a phone held sideways can shrink its panels without shrinking a touch target below 44 px.
 */

export type HudLayout = 'landscape' | 'touch' | 'portrait';

export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** A scaled frame: its box on screen (CSS px) and the zoom its board-sized contents are drawn at. */
export interface Frame {
  left: number;
  top: number;
  width: number;
  height: number;
  zoom: number;
}

export interface LayoutPlan {
  layout: HudLayout;
  panels: Frame;
  controls: Frame;
}

/** Board sizes (frames 01 and 04). */
export const BOARD = { landscape: { w: 1280, h: 720 }, portrait: { w: 390, h: 844 } } as const;

/**
 * Frame 04 is an iPhone 13 screen whose 54 px top and 34 px bottom already clear its notch and home bar, so those
 * insets are part of the board; frame 01 assumes none (judgment call 27).
 */
const BOARD_INSETS: Record<'landscape' | 'portrait', Insets> = {
  landscape: { top: 0, right: 0, bottom: 0, left: 0 },
  portrait: { top: 47, right: 0, bottom: 34, left: 0 },
};

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Portrait when the screen is taller than wide (the camera's rule, judgment call 2); touch adds the controls. */
export function chooseLayout(w: number, h: number, touch: boolean): HudLayout {
  if (h > w) return 'portrait';
  return touch ? 'touch' : 'landscape';
}

/** Steering pad width (frame 04), the fire button's (smaller than the board's 128 px, Mehdi 2026-10-10), and the least
 * gap kept between them. */
export const PAD_W = 196;
export const FIRE_W = 100;
const CONTROL_GAP = 10;
/** Frame 04's side margins: 16 px beside the pad, 22 px beside the fire button. */
const EDGES = 16 + 22;

/** Panel and control scales (judgment call 26). */
export function scales(layout: HudLayout, w: number, h: number): { panels: number; controls: number } {
  if (layout === 'portrait') {
    // Phones stay at 1× (a large phone gets more view, not bigger panels); tablets scale up to 1.5×.
    const s = Math.min(w, h) < 600 ? 1 : clamp(Math.min(w / BOARD.portrait.w, h / BOARD.portrait.h), 1, 1.5);
    // Only a screen too narrow for both controls at full size (under 354 px) shrinks them.
    return { panels: s, controls: Math.min(s, w / (PAD_W + FIRE_W + CONTROL_GAP + 20)) };
  }
  const fit = Math.min(w / BOARD.landscape.w, h / BOARD.landscape.h);
  if (layout === 'touch') {
    const panels = Math.max(0.6, Math.min(clamp(fit, 0.75, 1.5), rowFit(w)));
    return { panels, controls: clamp(Math.min(w, h) / BOARD.portrait.w, 1, 1.5) };
  }
  const panels = Math.max(0.6, Math.min(clamp(fit, 0.75, 2.5), rowFit(w)));
  return { panels, controls: Math.max(1, panels) };
}

/**
 * The largest panel scale that keeps the landscape top row apart on a screen w px wide: half the centred level panel
 * (164 px at 1×) and the fuel panel with its margin (32 + 320 px) must fit in half the screen, 8 px apart.
 */
function rowFit(w: number): number {
  return (w / 2 - 8) / (164 + 352);
}

function frame(w: number, h: number, extra: Insets, zoom: number): Frame {
  return {
    left: extra.left,
    top: extra.top,
    width: Math.max(0, w - extra.left - extra.right),
    height: Math.max(0, h - extra.top - extra.bottom),
    zoom,
  };
}

/** The frames for a screen of w × h CSS px with the given safe-area insets. */
export function planLayout(w: number, h: number, insets: Insets, touch: boolean): LayoutPlan {
  const layout = chooseLayout(w, h, touch);
  const ref = BOARD_INSETS[layout === 'portrait' ? 'portrait' : 'landscape'];
  // Each inset moves the HUD only by what it exceeds the board's own (judgment call 27).
  const extra: Insets = {
    top: Math.max(0, insets.top - ref.top),
    right: Math.max(0, insets.right - ref.right),
    bottom: Math.max(0, insets.bottom - ref.bottom),
    left: Math.max(0, insets.left - ref.left),
  };
  const s = scales(layout, w, h);
  return { layout, panels: frame(w, h, extra, s.panels), controls: frame(w, h, extra, s.controls) };
}

/**
 * How much of frame 04's side margins the touch controls keep (1 = all of them): a screen narrower than the board
 * moves the pad and the fire button towards its edges before they would come closer than 10 px to each other.
 */
export function controlEdges(designWidth: number): number {
  const free = designWidth - PAD_W - FIRE_W - CONTROL_GAP;
  return clamp(free / EDGES, 0.26, 1);
}
